import { Check, CloudCog, Info } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';

function useEnglishInterface() {
  const context = useOutletContext() || {};
  return context.language === 'en-US';
}

export function SettingsPageHeader({ eyebrow, title, description, status = '个性化设置' }) {
  return (
    <section className="account-page-heading">
      <div>
        <span className="account-eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <span className="account-status account-status--warm">{status}</span>
    </section>
  );
}

export function SettingsPreviewNotice({
  title,
  children,
  actionLabel,
  onAction,
}) {
  const english = useEnglishInterface();
  const resolvedTitle = title || (english ? 'Try the feel first' : '先试试手感');
  return (
    <section className="creation-settings-notice">
      <CloudCog size={17} />
      <div>
        <strong>{resolvedTitle}</strong>
        <span>{children || (english ? 'Changes appear here immediately and do not affect your active project before saving.' : '调整会即时显示在这里；正式保存前，不会影响你正在创作的项目。')}</span>
      </div>
      {actionLabel && (
        <button type="button" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </section>
  );
}

export function SettingsCard({ eyebrow, title, description, aside, children, className = '' }) {
  return (
    <section className={`creation-settings-card${className ? ` ${className}` : ''}`}>
      <header>
        <div>
          {eyebrow && <span className="account-eyebrow">{eyebrow}</span>}
          <h2>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        {aside && <span>{aside}</span>}
      </header>
      <div className="creation-settings-card__body">{children}</div>
    </section>
  );
}

export function SettingsRow({
  icon: Icon,
  title,
  description,
  scope,
  children,
  locked = false,
}) {
  const english = useEnglishInterface();
  const resolvedScope = scope || (english ? 'Personal' : '个人');
  return (
    <div className={`creation-settings-row${locked ? ' is-locked' : ''}`}>
      <span className="creation-settings-row__icon">{Icon && <Icon size={16} />}</span>
      <div className="creation-settings-row__copy">
        <strong>{title}</strong>
        <span>{description}</span>
      </div>
      <span className="creation-settings-row__scope">{resolvedScope}</span>
      <div className="creation-settings-row__control">{children}</div>
    </div>
  );
}

export function Toggle({ checked, onChange, label, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-label={label}
      aria-checked={checked}
      className={`creation-toggle${checked ? ' is-on' : ''}`}
      onClick={() => !disabled && onChange?.(!checked)}
      disabled={disabled}
    >
      <i />
    </button>
  );
}

export function Segmented({ label, value, options, onChange, disabled = false }) {
  return (
    <div className="creation-segmented" role="radiogroup" aria-label={label}>
      {options.map(option => {
        const optionValue = typeof option === 'object' ? option.value : option;
        const optionLabel = typeof option === 'object' ? option.label : option;
        return (
          <button
            type="button"
            role="radio"
            aria-checked={value === optionValue}
            className={value === optionValue ? 'is-active' : ''}
            onClick={() => !disabled && onChange?.(optionValue)}
            disabled={disabled}
            key={optionValue}
          >
            {optionLabel}
          </button>
        );
      })}
    </div>
  );
}

export function ChoiceCards({ label, value, options, onChange, columns = 3 }) {
  return (
    <div
      className="creation-choice-grid"
      style={{ '--choice-columns': columns }}
      role="radiogroup"
      aria-label={label}
    >
      {options.map(option => {
        const selected = value === option.value;
        const Icon = option.icon;
        return (
          <button
            type="button"
            role="radio"
            aria-checked={selected}
            className={selected ? 'is-selected' : ''}
            onClick={() => onChange?.(option.value)}
            key={option.value}
          >
            <span>{Icon && <Icon size={17} />}</span>
            <div>
              <strong>{option.label}</strong>
              <small>{option.description}</small>
            </div>
            <i>{selected && <Check size={11} />}</i>
          </button>
        );
      })}
    </div>
  );
}

export function SystemRule({ title, description, status = '系统规则' }) {
  return (
    <article className="creation-system-rule">
      <span><Check size={13} /></span>
      <div>
        <strong>{title}</strong>
        <small>{description}</small>
      </div>
      <em>{status}</em>
    </article>
  );
}

export function SettingsScopeNote({ children }) {
  return (
    <div className="creation-settings-scope-note">
      <Info size={14} />
      <span>{children}</span>
    </div>
  );
}
